// src/features/media/components/VideoAvailabilityFields.tsx

import { Stack, Select, Group, Text } from '@mantine/core';
import { DateTimePicker } from '@mantine/dates';
import { useController, type Control } from 'react-hook-form';
import { DateTime } from 'luxon';
import type { VideoFormData } from '../schemas/VideoForm.schema';
import type { StartAvailabilityMode, EndAvailabilityMode } from '@/lib/types/Media';

interface VideoAvailabilityFieldsProps {
  control: Control<VideoFormData>;
}

/**
 * Convert Unix timestamp (seconds, UTC) to DateTimePicker string value.
 * Luxon fromSeconds() uses system local timezone by default.
 * Format: 'YYYY-MM-DD HH:mm:ss' (Mantine 8 date string format)
 */
function timestampToDateStr(ts: number | null | undefined): string | null {
  if (typeof ts !== 'number' || !isFinite(ts)) return null;
  return DateTime.fromSeconds(ts).toFormat('yyyy-MM-dd HH:mm:ss');
}

/**
 * Convert DateTimePicker string value back to Unix timestamp (seconds, UTC).
 * Parses local timezone, then converts to UTC epoch seconds.
 */
function dateStrToTimestamp(dateStr: string): number {
  return Math.floor(DateTime.fromFormat(dateStr, 'yyyy-MM-dd HH:mm:ss').toUTC().toSeconds());
}

/**
 * Check if a field value represents "has a timestamp" (non-null finite number).
 */
function hasTimestamp(value: unknown): value is number {
  return typeof value === 'number' && isFinite(value);
}

export function VideoAvailabilityFields({ control }: VideoAvailabilityFieldsProps) {
  // Start availability
  const { field: availStartField } = useController({
    control,
    name: 'availStart',
  });

  // Derive mode: if value is a valid timestamp → show picker; otherwise → "Now"
  const startMode: StartAvailabilityMode = hasTimestamp(availStartField.value)
    ? 'on_or_after'
    : 'now';

  const handleStartModeChange = (mode: string | null) => {
    if (mode === 'now') {
      availStartField.onChange(null);
    } else {
      availStartField.onChange(Math.floor(DateTime.now().toSeconds()));
    }
  };

  // End availability
  const { field: availEndField } = useController({
    control,
    name: 'availEnd',
  });

  const endMode: EndAvailabilityMode = hasTimestamp(availEndField.value)
    ? 'until'
    : 'no_end';

  const handleEndModeChange = (mode: string | null) => {
    if (mode === 'no_end') {
      availEndField.onChange(null);
    } else {
      availEndField.onChange(Math.floor(DateTime.now().plus({ days: 30 }).toSeconds()));
    }
  };

  return (
    <Stack gap="md">
      <Text size="sm" fw={500}>Availability</Text>

      {/* Start Availability */}
      <Group grow align="flex-start">
        <Select
          label="Start"
          value={startMode}
          onChange={handleStartModeChange}
          data={[
            { value: 'now', label: 'Now (immediately available)' },
            { value: 'on_or_after', label: 'On Or After...' },
          ]}
        />

        {startMode === 'on_or_after' && hasTimestamp(availStartField.value) && (
          <DateTimePicker
            label="Available from"
            value={timestampToDateStr(availStartField.value)}
            onChange={(val) => {
              if (val) {
                availStartField.onChange(dateStrToTimestamp(val));
              }
            }}
            valueFormat="MMM D, YYYY h:mm A"
            timePickerProps={{ format: '12h' }}
          />
        )}
      </Group>

      {/* End Availability */}
      <Group grow align="flex-start">
        <Select
          label="End"
          value={endMode}
          onChange={handleEndModeChange}
          data={[
            { value: 'no_end', label: 'No End (always available)' },
            { value: 'until', label: 'Until...' },
          ]}
        />

        {endMode === 'until' && hasTimestamp(availEndField.value) && (
          <DateTimePicker
            label="Available until"
            value={timestampToDateStr(availEndField.value)}
            onChange={(val) => {
              if (val) {
                availEndField.onChange(dateStrToTimestamp(val));
              }
            }}
            valueFormat="MMM D, YYYY h:mm A"
            timePickerProps={{ format: '12h' }}
          />
        )}
      </Group>
    </Stack>
  );
}
